import { defineComponent as _defineComponent } from 'vue'
import { computed, onMounted, ref } from 'vue';
import { useSettingsBreadcrumbs } from '@/mixins/settings-breadcrumbs';
import { isModelEqualToModel, cloneModel } from '@/model/model-utils';
import { performAction, performSaveAction } from '@/services/vue-utils';
import AccountSettings from '@/model/AccountSettings';
import SettingsForm from '@/components/layouts/SettingsForm.vue';
import SettingsGroup from '@/components/layouts/SettingsGroup.vue';
import WebsiteQrDialog from '@/components/views/settings/WebsiteQrDialog.vue';
import SettingsButtonBlock from '@/components/forms/SettingsButtonBlock.vue';
import InputDialog from '@/components/dialogs/InputDialog.vue';
import grpcClient from '@/grpc-api/grpc-client';
import Campaign from '@/grpc-api/model/campaign';
import { ValidationObserver, extend } from 'vee-validate';
import { hostedWidgetURL, WIDGET_DOMAIN } from '@/services/configuration';
import useUnsavedGuard from '@/composables/unsaved-guard';
import store from '@/store/store';
import i18n from '@/plugins/i18n';
import useStoreWatch from '@/composables/store-watch';
import PaywallSwitch from '@/components/buttons/PaywallSwitch.vue';
import { ApiErrorCode } from '@/api/api-error';
import { partysizes, useDaysInAdvanceItems, useTimeInAdvanceItems } from './settings/helpers/settings-values';


export default /*#__PURE__*/_defineComponent({
  __name: 'OnlineSettings',
  setup(__props) {

const observerRef = ref(null as null | InstanceType<typeof ValidationObserver>);

const a = ref(new AccountSettings());
const olda = ref(new AccountSettings());

const qrDialogVisible = ref(false);

const isWaitlistFeatureAllowed = computed<boolean>(() => store.getters.isWaitlistFeatureAllowed);

// Unsaved Guard
const isChanged = computed(() => !isModelEqualToModel(a.value, olda.value));
useUnsavedGuard(isChanged);

const widgetSubdomain = ref(null as string | null);

const widgetDocumentationURL = computed(() => store.state.settings.account.widgetDocumentationURL || '');

const maxOnlineCapacity = computed(() => store.getters.maxOnlineCapacity);

const maxGuestsItems = computed(() => [...new Array(501)].map((e, i) => ({
  text: i === 0 ? i18n.t('label.maximum_x', [maxOnlineCapacity.value]) : `${i}`,
  value: i,
})));

const maxReservationsItems = computed(() => [...new Array(501)].map((e, i) => ({
  text: i === 0 ? i18n.t('label.maximum') : `${i}`,
  value: i,
})));

// Campaign
const campaign = ref(null as Campaign | null);

const loadCampaign = async (): Promise<boolean> => {
  const ok = await performAction(
    null,
    i18n.tc('error.load_data'),
    async () => {
      campaign.value = await grpcClient.getDefaultCampaign();
      widgetSubdomain.value = campaign.value.subdomain ?? null;
    },
  );
  return ok;
};

const updateCampaign = async (c: Campaign): Promise<boolean> => {
  const ok = await performAction(
    null,
    i18n.tc('error.save_data'),
    async () => {
      campaign.value = await grpcClient.updateDefaultCampaign(c);
    },
  );
  return ok;
};

// widget dialog
const urlEditorVisible = ref(false);

const hostedFormUrl = computed(() => {
  const subdomain = campaign.value?.subdomain;
  return subdomain ? hostedWidgetURL({ subdomain }) : store.getters.hostedWidgetURL ?? 'N/A';
});

const openURLEditor = async () => {
  const ok = await loadCampaign();
  if (ok) urlEditorVisible.value = true;
};

const saveURLEditor = async () => {
  if (!campaign.value) { urlEditorVisible.value = false; return; }

  const nc = cloneModel(campaign.value);
  nc.subdomain = widgetSubdomain.value ?? undefined;

  const ok = await updateCampaign(nc);
  if (ok) urlEditorVisible.value = false;
};

// store watch, load data
const reset = async () => {
  const as = store.state.settings.accountSettings;
  a.value = cloneModel(as);
  olda.value = as;

  campaign.value = null;
  widgetSubdomain.value = null;
  if (!store.getters.isLoaded) return;

  await loadCampaign();
};
useStoreWatch(reset, true);

const save = async () => {
  const ok = await performSaveAction(
    observerRef.value,
    async () => store.dispatch('sendAccountSettings', { entity: a.value }),
  );

  if (ok) reset();
};

onMounted(() => {
  const testCampaign = async (c: Campaign): Promise<string|boolean> => {
    try {
      await grpcClient.testDefaultCampaign(c);
    } catch (e: any) {
      if (e.code === ApiErrorCode.already_exists || e.code === ApiErrorCode.subdomain_already_exists) {
        return i18n.tc('error.url_already_taken');
      }
      if (e.code === ApiErrorCode.invalid_argument || e.code === ApiErrorCode.subdomain_invalid) {
        return i18n.tc('error.url_not_allowed');
      }
      return true;
    }
    return true;
  };

  extend('unique_widget_url', {
    validate: async (value, _) => {
      if (campaign.value === null || value === undefined) return true;
      const nc = cloneModel(campaign.value);
      nc.subdomain = value;
      return testCampaign(nc);
    },
    lazy: true,
  });
});


return { __sfc: true,observerRef, a, olda, qrDialogVisible, isWaitlistFeatureAllowed, isChanged, widgetSubdomain, widgetDocumentationURL, maxOnlineCapacity, maxGuestsItems, maxReservationsItems, campaign, loadCampaign, updateCampaign, urlEditorVisible, hostedFormUrl, openURLEditor, saveURLEditor, reset, save, useSettingsBreadcrumbs, SettingsForm, SettingsGroup, WebsiteQrDialog, SettingsButtonBlock, InputDialog, ValidationObserver, WIDGET_DOMAIN, PaywallSwitch, partysizes, useDaysInAdvanceItems, useTimeInAdvanceItems }
}

})